import { Building2, CircleCheck } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { EMPTY_VALUE } from "@/constants";
import { orEmptyValue } from "@/lib/hcbb-cost-estimator-utils";
import {
  HCBB_DETAIL_HEADING_ID,
  HcbbDetailAddress,
  HcbbDetailHeader,
  HcbbDetailLine,
} from "@/pages/components/hcbb-cost-estimator/hcbb-detail-header";
import { HcbbPhoneButton } from "@/pages/components/hcbb-cost-estimator/hcbb-phone-button";
import { HcbbProviderCredentials } from "@/pages/components/hcbb-cost-estimator/hcbb-provider-credentials";
import { HcbbProviderOffices } from "@/pages/components/hcbb-cost-estimator/hcbb-provider-offices";
import { HcbbTruncatedText } from "@/pages/components/hcbb-cost-estimator/hcbb-truncated-text";
import type { HcbbProfessionalResult } from "@/model/hcbb-cost-estimator";

/** Everything the details endpoint knows about one provider. */
export const HcbbProviderSummary = ({
  professional,
}: {
  professional: HcbbProfessionalResult;
}) => {
  const specialty = orEmptyValue(professional.specialty);

  return (
    <section
      aria-labelledby={HCBB_DETAIL_HEADING_ID}
      className="border-1 border-gray space-y-6 rounded-lg border p-6 shadow-md"
    >
      <HcbbDetailHeader
        title={professional.displayName}
        leading={<ProviderPhoto professional={professional} />}
        badge={
          // A specialty the payload sent as "NA" is no specialty at all.
          specialty !== EMPTY_VALUE ? (
            <Badge className="rounded-full bg-gray-200 font-light text-gray-800">
              {specialty}
            </Badge>
          ) : undefined
        }
        actions={
          <>
            {professional.acceptingNewPatients && <AcceptingNewPatients />}
            <HcbbPhoneButton
              name={professional.displayName}
              phone={professional.phone}
            />
          </>
        }
      >
        {/* The practice name is the one field long enough to break the row. */}
        <HcbbDetailLine icon={Building2}>
          <HcbbTruncatedText text={orEmptyValue(professional.facilityName)} />
        </HcbbDetailLine>
        <HcbbDetailAddress
          address={professional.address}
          displayRadius={professional.displayRadius}
        />
      </HcbbDetailHeader>

      <HcbbProviderCredentials professional={professional} />
      <HcbbProviderOffices offices={professional.offices} />
    </section>
  );
};

/**
 * Decorative: the name it sits beside is the page's heading, so announcing the
 * photo as well would only repeat it. `aria-hidden` on the root covers the
 * initials fallback too.
 */
const ProviderPhoto = ({
  professional,
}: {
  professional: HcbbProfessionalResult;
}) => {
  const photoUrl = orEmptyValue(professional.photoUrl);

  return (
    <Avatar aria-hidden="true" className="size-16">
      {photoUrl !== EMPTY_VALUE && <AvatarImage src={photoUrl} alt="" />}
      <AvatarFallback>{toInitials(professional.displayName)}</AvatarFallback>
    </Avatar>
  );
};

/** Up to two initials, or nothing at all rather than a stray character. */
const toInitials = (name: string): string =>
  name
    .split(" ")
    .map((part) => part.trim()[0] ?? "")
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

/**
 * Never colour alone — the words carry the meaning and the tick reinforces it
 * (1.4.1 Use of Color). `green-700` clears 4.5:1 on white; `green-600` does
 * not at this size.
 */
const AcceptingNewPatients = () => (
  <p className="flex items-center gap-1 text-xs font-medium uppercase text-green-700">
    <CircleCheck aria-hidden="true" height={16} className="shrink-0" />
    Accepting new patients
  </p>
);
